import React, {ReactNode, RefObject, useCallback, useContext, useEffect, useRef} from 'react';
import {ExpectedError} from '@di-shared/features/common/CustomError';

export interface ScrollSpyProviderProps {
  rootMargin?: string;
  selectedClassName?: string;
  isHash?: boolean;
  scrollOffset?: number | (() => number);
  children: ReactNode;
}

interface ScrollSpyContextState {
  scrollWrapperRef: RefObject<HTMLDivElement>;

  sectionTo(id: string | number): void;

  getSectionProps(id: string | number): {[key: string]: string};

  getSectionHeaderProps(id: string | number): {[key: string]: string};
}

const ScrollSpyContext = React.createContext<ScrollSpyContextState>({
  // @ts-ignore
  scrollWrapperRef: null,
  sectionTo() {
    throw new ExpectedError('Provider required');
  },
  getSectionProps() {
    throw new ExpectedError('Provider required');
  },
  getSectionHeaderProps() {
    throw new ExpectedError('Provider required');
  },
});

const ID_PREFIX = '_';

/**
 * from configurator
 * 수정 시, 컨피그레이터 담당자에게 한번 노티주세요
 *
 * @param rootMargin
 * @param selectedClassName
 * @param isHash
 * @param scrollOffset
 * @param children
 * @constructor
 */
function ScrollSpyProvider({
  rootMargin = '-50% 0px',
  selectedClassName = 'is-selected',
  isHash = false,
  scrollOffset = 0,
  children,
}: ScrollSpyProviderProps): JSX.Element {
  const sectionKeyRef = useRef<string>(getDatasetKey());
  const sectionHeaderKeyRef = useRef<string>(sectionKeyRef.current + '-header');
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  const sectionTo = useCallback((id: string | number) => {
    const element = document.querySelector(`[${sectionKeyRef.current}=${ID_PREFIX}${id}]`);
    if (!element) {
      return;
    }

    const offset = typeof scrollOffset === 'function' ? scrollOffset() : scrollOffset;
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollTo({
        top:
          element.getBoundingClientRect().top -
          scrollWrapperRef.current.getBoundingClientRect().top +
          scrollWrapperRef.current.scrollTop +
          offset,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY + offset,
        behavior: 'smooth',
      });
    }
  }, []);

  const getSectionProps = useCallback((id: string | number) => {
    return {[sectionKeyRef.current]: `${ID_PREFIX}${id}`};
  }, []);

  const getSectionHeaderProps = useCallback((id: string | number) => {
    return {[sectionHeaderKeyRef.current]: `${ID_PREFIX}${id}`};
  }, []);

  useEffect(() => {
    const initSectionName = window.location.hash ? window.location.hash.replace(`#${ID_PREFIX}`, '') : null;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const hitEntry = entries.find((entry) => entry.isIntersecting);
        if (!hitEntry) {
          return;
        }
        const selectedId = hitEntry.target.getAttribute(sectionKeyRef.current) || '';
        /**
         * Provider 내의 state를 교체하는 방식도 있지만, 그러면 내부 재평가가 빈번해져서 가능한 영향도를 줄이기 위해 돔을 직접제어함
         */
        document.querySelectorAll(`[${sectionHeaderKeyRef.current}]`).forEach((element) => element.classList.remove(selectedClassName));
        const targetTab = document.querySelector(`[${sectionHeaderKeyRef.current}=${selectedId}]`);
        if (targetTab) {
          targetTab.classList.add(selectedClassName);
          targetTab.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
        }
        if (isHash) {
          window.history.replaceState(null, '', `#${selectedId}`);
        }
      },
      {rootMargin}
    );

    /**
     * 동적으로 생성되는 step 들이 변경 될때마다 intersection observer 가 갱신되어야함
     * 문제는 값으로 해당 타이밍을 잡으면 실제 dom 을 못찾을때가 있음
     * 그래서 그냥 루트 감시해서 변경될때마다 최신화함
     */
    const mutationObserver = new MutationObserver(() => {
      addObserveEvent();
    });

    mutationObserver.observe(document.body, {childList: true, subtree: true});

    if (initSectionName) {
      setTimeout(() => {
        sectionTo(initSectionName);
      }, 400);
    }

    function addObserveEvent() {
      const observeTargets = document.querySelectorAll(`[${sectionKeyRef.current}]`);
      intersectionObserver.disconnect();
      observeTargets.forEach((target) => {
        intersectionObserver.observe(target);
      });
    }

    addObserveEvent();

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [rootMargin, selectedClassName]);

  return (
    <ScrollSpyContext.Provider
      value={{
        sectionTo,
        getSectionProps,
        getSectionHeaderProps,
        scrollWrapperRef,
      }}
    >
      {children}
    </ScrollSpyContext.Provider>
  );
}

export function useScrollSpy() {
  const {scrollWrapperRef, sectionTo, getSectionProps, getSectionHeaderProps} = useContext(ScrollSpyContext);
  return {scrollWrapperRef, sectionTo, getSectionProps, getSectionHeaderProps};
}

let id = 0;

function getDatasetKey() {
  return `data-observe${++id}`;
}

export default ScrollSpyProvider;
